import { Component } from '@angular/core';
import { IonicPage, ModalController, NavController, NavParams, ToastController } from 'ionic-angular';
import {
  DailyLogOperationInstance,
  DailyOperationLogRecord,
  DelayAffect,
  DelayChoice,
  OperationType,
  StepInstance
} from '../../../models/projects/operation.models';
import { OperationService } from '../../../services/operations/operation/operation.service';
import { DelayService } from '../../../services/delay/delay-service/delay.service';
import { DailyLogModalPage } from '../../daily-log/daily-log-modal/daily-log-modal';
import { DailyLogPage } from '../../daily-log/daily-log/daily-log';
import { NavMainMenuPage } from '../../nav/nav-menus/nav-main-menu/nav-main-menu';
import { NavHelperService } from '../../../services/nav/nav-helper.service';
import { DailyLogService } from '../../../services/daily-log/daily-log.service';
import { PtLuxonDate } from '../../../models/projects/date.models';
import { OperationSaveService } from '../../../services/operations/operation-save/operation-save.service';
import { cloneDeep } from 'lodash';
import { AppDataSyncService } from '../../../services/app-data-sync/app-data-sync.service';
import { DelaySaveHelper } from '../../../models/app-data/app-data';
import { DateService } from '../../../services/date/date.service';
import { TimeZoneService } from '../../../services/time-zone/time-zone.service';
import { VesselService } from '../../../services/vessel/vessel.service';
import { StepService } from '../../../services/step/step.service';
import { JackingActivityService } from '../../../services/jacking-activity/jacking-activity.service';
import { AppConfig } from '../../../app/app.config';

@IonicPage()
@Component({
  selector: 'page-affected-operations',
  templateUrl: 'affected-operations.html',
})

export class AffectedOperationsPage {
  config = AppConfig.settings;
  instance: DailyLogOperationInstance;
  originalInstanceCopy: DailyLogOperationInstance;
  type: OperationType;
  saved: boolean;
  oldStart: PtLuxonDate;
  oldEnd: PtLuxonDate;
  dailyLogDate: PtLuxonDate;
  choicesMade: boolean = true;
  concurrentInstances: DailyLogOperationInstance[] = [];
  contractualInstances: DailyLogOperationInstance[] = [];
  allInstances: DailyLogOperationInstance[] = [];
  cancelledInstances: DailyLogOperationInstance[] = [];
  switchedInstances: DailyLogOperationInstance[] = [];
  pausedInstances: DailyLogOperationInstance[] = [];
  continuedInstances: DailyLogOperationInstance[] = [];
  affectedContractualOperations: DelayAffect[] = [];
  affectedConcurrentOperations: DelayAffect[] = [];
  changedChoices: DelayAffect[] = [];
  loading = false;
  interruption = OperationType.Interruption;

  constructor(public navCtrl: NavController,
              public navParams: NavParams,
              public operationService: OperationService,
              public modalController: ModalController,
              public delayService: DelayService,
              public navHelperService: NavHelperService,
              public dailyLogService: DailyLogService,
              public operationSaveService: OperationSaveService,
              public appDataSyncService: AppDataSyncService,
              private dateService: DateService,
              private toastCtrl: ToastController,
              private timeZoneService: TimeZoneService,
              private vesselService: VesselService,
              private stepService: StepService,
              private jackingActivityService: JackingActivityService,
  ) {
    this.instance = navParams.get('instance');
    this.originalInstanceCopy = navParams.get('originalInstanceCopy'); // need a copy to reliably fix affected operation pause times when editing a delay
    this.type = navParams.get('type');
    this.dailyLogDate = navParams.get('dailyLogDate');

    if (this.type === OperationType.Interruption) {
      this.instance.isInterruption = true;
    }

    this.oldStart = cloneDeep(this.instance.startTime);
    this.oldEnd = cloneDeep(this.instance.endTime);
  }

  async ionViewDidLoad() {
    this.loading = true;
    this.changedChoices = [];

    await this.setInstanceTypes();
    await this.setAffectedContractualOperations();
    await this.setAffectedConcurrentOperations();
    await this.setChoices();
    this.checkChoicesMade();
    this.loading = false;
  }

  // assigns the type (contractual/concurrent) depending on what that instance type was at the start of the delay
  // this then has an effect on what buttons/actions are displayed to the user
  async setInstanceTypes() {
    const affectedOperations = await this.delayService
      .findPotentiallyAffectedOperations(cloneDeep(this.instance), this.dailyLogDate);

    this.allInstances = affectedOperations;
    this.affectedConcurrentOperations = [] as DelayAffect[];

    for (let i = 0; i < this.allInstances.length; i++) {
      const type = await this.getTypeAtDelayStart(this.allInstances[i]);

      switch (type) {
        case OperationType.Concurrent:
          this.concurrentInstances.push(this.allInstances[i])
          break;
        case OperationType.Contractual:
          this.contractualInstances.push(this.allInstances[i])
          break;
        // these 2 cases below should never return
        case OperationType.Delay:
        case OperationType.Interruption:
          break;
      }
    }
  }

  async getTypeAtDelayStart(instance: DailyLogOperationInstance): Promise<OperationType> {
    const switchesBeforeDelay = instance.switchTimes
      ? instance.switchTimes.filter(s => s.date.dateTime < this.instance.startTime.dateTime)
      : null;

    if (!instance.switchTimes || instance.switchTimes.length === 0 || !switchesBeforeDelay || switchesBeforeDelay.length === 0) {
      if (instance.isConcurrentOperation || instance.isInterruption) {
        return OperationType.Concurrent;
      } else if (!instance.isInterruption) {
        return OperationType.Contractual;
      }
    }

    const mostRecentSwitchBeforeDelay = switchesBeforeDelay
      .sort((function (a, b) {
        // sort by date descending
        return b.date.dateTime.toMillis() - a.date.dateTime.toMillis();
      }))[0];

    return mostRecentSwitchBeforeDelay.isConcurrent ? OperationType.Concurrent : OperationType.Contractual;
  }

  async setAffectedContractualOperations() {
    this.affectedContractualOperations = [] as DelayAffect[];

    for (let i = 0; i < this.contractualInstances.length; i++) {
      const affectedOperation = new DelayAffect();
      affectedOperation.instance = this.contractualInstances[i];
      affectedOperation.chosenSteps = await this.affectedSteps(affectedOperation.instance);
      affectedOperation.instance.startTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(affectedOperation.instance.startTime);
      affectedOperation.instance.endTimeToShow = affectedOperation.instance.endTime
        ? this.dateService.setTimeStringFromPtLuxonDate(affectedOperation.instance.endTime)
        : 'No End Time';

      if (affectedOperation.instance.stepCollectionInstance && affectedOperation.instance.stepCollectionInstance.stepInstances) {
        for (let j = 0; j < affectedOperation.instance.stepCollectionInstance.stepInstances.length; j++) {
          const step = affectedOperation.instance.stepCollectionInstance.stepInstances[j];
          if (step.startTime && step.startTime.dateTime) {
            step.startTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(step.startTime);
          }
          if (step.endTime && step.endTime.dateTime) {
            step.endTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(step.endTime);
          }
        }
      }

      this.affectedContractualOperations.push(affectedOperation);
    }
  }

  async setAffectedConcurrentOperations() {
    this.affectedConcurrentOperations = [] as DelayAffect[];

    for (let i = 0; i < this.concurrentInstances.length; i++) {
      const affectedOperation = new DelayAffect();
      affectedOperation.instance = this.concurrentInstances[i];
      affectedOperation.chosenSteps = await this.affectedSteps(affectedOperation.instance);
      affectedOperation.instance.startTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(affectedOperation.instance.startTime);
      affectedOperation.instance.endTimeToShow = affectedOperation.instance.endTime
        ? this.dateService.setTimeStringFromPtLuxonDate(affectedOperation.instance.endTime)
        : 'No End Time';

      if (affectedOperation.instance.stepCollectionInstance && affectedOperation.instance.stepCollectionInstance.stepInstances) {
        for (let j = 0; j < affectedOperation.instance.stepCollectionInstance.stepInstances.length; j++) {
          const step = affectedOperation.instance.stepCollectionInstance.stepInstances[j];
          if (step.startTime && step.startTime.dateTime) {
            step.startTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(step.startTime);
          }
          if (step.endTime && step.endTime.dateTime) {
            step.endTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(step.endTime);
          }
        }
      }

      this.affectedConcurrentOperations.push(affectedOperation);
    }
  }

  contractualDelayChoice(position: number, choice: DelayChoice) {
    this.affectedContractualOperations[position].choice = choice;

    const effect = cloneDeep(this.affectedContractualOperations[position]);
    this.addEffect(effect);

    this.checkChoicesMade();
  }

  concurrentDelayChoice(position: number, choice: DelayChoice) {
    this.affectedConcurrentOperations[position].choice = choice;

    const effect = cloneDeep(this.affectedConcurrentOperations[position]);
    this.addEffect(effect);

    this.checkChoicesMade();
  }

  openDailyLogModal() {
    const dailyLogModal = this.modalController.create(DailyLogModalPage);
    dailyLogModal.present();
  }

  checkChoicesMade() {
    let valid = true;

    this.affectedConcurrentOperations.forEach(element => {
      if (element.choice == null) {
        valid = false;
        return;
      }
    });

    if (this.type !== OperationType.Interruption) {
      this.affectedContractualOperations.forEach(element => {
        if (element.choice == null) {
          valid = false;
          return;
        }
      });
    }

    this.choicesMade = valid;
  }

  async setChoices() {
    this.affectedConcurrentOperations.forEach(element => {
      this.assignChoice(element);
    });

    this.affectedContractualOperations.forEach(element => {
      this.assignChoice(element);
    });
  }

  assignChoice(element: DelayAffect) {
    if (!this.checkInstance(element.instance.id)) {
      return;
    }

    if (element.instance.delayLinks && this.instance.id && element.instance.delayLinks.some(e => e.delayId === this.instance.id)) {
      const delayLink = element.instance.delayLinks.find(e => e.delayId === this.instance.id);

      // type ids on api are as follows
      // Continued = 1,
      // Paused = 2,
      // Switched = 3,
      // Cancelled = 4

      switch (delayLink.typeId) {
        case 'continued':
          element.choice = DelayChoice.play;
          break;
        case 'paused':
          element.choice = DelayChoice.pause;
          break;
        case 'switched':
          element.choice = DelayChoice.switch;
          break;
        case 'cancelled':
          element.choice = DelayChoice.cancel;
          break;
        default:
          // there is a sys_default of 0 to prevent foreign key constraint when this applies to data already in the system
          // should never be hit going forward
          break;
      }

      return;
    }
  }

  async save() {
    this.loading = true;
    let delayData = new DelaySaveHelper();
    delayData.dailyLogDate = this.dailyLogDate.dateTime;
    delayData.vesselId = (await this.vesselService.getEditor()).id;
    delayData.delay = this.instance;

    delayData.pauseTimeIdsToDelete = await this.checkExistingPauseTimesToRemove(delayData);

    // todo - filter instances based on cancel/pause/switch choice
    this.cancelledInstances = this.affectedConcurrentOperations
      .filter(p => p.choice === DelayChoice.cancel)
      .concat(this.affectedContractualOperations
        .filter(p => p.choice === DelayChoice.cancel))
      .map(p => p.instance);

    this.pausedInstances = this.affectedConcurrentOperations
      .filter(p => p.choice === DelayChoice.pause)
      .concat(this.affectedContractualOperations
        .filter(p => p.choice === DelayChoice.pause))
      .map(p => p.instance);

    this.switchedInstances = this.affectedConcurrentOperations
      .filter(p => p.choice === DelayChoice.switch)
      .concat(this.affectedContractualOperations
        .filter(p => p.choice === DelayChoice.switch))
      .map(p => p.instance);

    this.continuedInstances = this.affectedConcurrentOperations
      .filter(p => p.choice === DelayChoice.play)
      .map(p => p.instance);

    // clone instance to fix problem where instance was having offset added multiple times in addLocalOffsetsToDelaySaveHelper method
    const instanceCopy = cloneDeep(this.instance);

    // cancel instances
    for (let i = 0; i < this.cancelledInstances.length; i++) {
      delayData = await this.delayService.cancelOperation(instanceCopy, this.cancelledInstances[i], delayData);
    }

    // pause instances
    for (let i = 0; i < this.pausedInstances.length; i++) {
      delayData = await this.delayService.pauseOperation(instanceCopy, this.pausedInstances[i], this.dailyLogDate, delayData, this.originalInstanceCopy);
    }

    // switch instances
    for (let i = 0; i < this.switchedInstances.length; i++) {
      delayData = await this.delayService.switchOperation(instanceCopy, this.switchedInstances[i], delayData);
    }

    delayData.continuedOperations = this.continuedInstances;

    // create delay daily log records
    const delayRecordData = await this.createDelayInstanceRecords(instanceCopy);
    delayData.delayLogRecords = delayData.delayLogRecords.concat(delayRecordData.delayLogRecords);
    delayData.dailyLogIdsToDelete = delayData.dailyLogIdsToDelete.concat(delayRecordData.dailyLogIdsToDelete);

    // call to save all changes
    await this.delayService.saveDelay(delayData);

    this.presentSaveToast().then(() => {
      this.navCtrl.setRoot(DailyLogPage);
      this.navHelperService.updateNavMenuPage({page: NavMainMenuPage});
    });
  }

  async createDelayInstanceRecords(instance: DailyLogOperationInstance): Promise<DelaySaveHelper> {
    const result = new DelaySaveHelper();
    const steps = !instance.stepCollectionInstance
      ? null
      : instance.stepCollectionInstance.stepInstances;

    let oldLogs = null as DailyOperationLogRecord[];

    try {
      oldLogs = await this.dailyLogService.getDailyLogDataForOperation(instance.id ? instance.id : instance.localId);
    } catch (error) {
      this.loading = false;
      throw (error);
    }

    try {
      result.delayLogRecords = await this.operationSaveService.saveOperation(instance, steps, oldLogs, instance, false, true);
    } catch (error) {
      this.loading = false;
      throw (error);
    }

    if (oldLogs) {
      for (let i = 0; i < oldLogs.length; i++) {
        // if oldlog is no longer valid make sure it gets deleted - dont think we need to worry about this in locked day mode, so check for id
        if (oldLogs[i].id && result.delayLogRecords.findIndex(e => e.id === oldLogs[i].id) === -1) {
          result.dailyLogIdsToDelete.push(oldLogs[i].id);
        }
      }
    }

    return result;
  }

  checkInstance(operationId: number) {
    let chosenInstance = this.affectedContractualOperations
      .filter(e => e.instance.id
        ? e.instance.id === operationId
        : e.instance.localId === operationId);

    if (!chosenInstance || chosenInstance.length === 0) {
      chosenInstance = this.affectedConcurrentOperations
        .filter(e => e.instance.id
          ? e.instance.id === operationId
          : e.instance.localId === operationId);
    }

    if (!chosenInstance || chosenInstance.length === 0) {
      return null;
    }

    return this.instance.id
      ? chosenInstance[0].instance.delayOperationApiIds.some(e => e === this.instance.id)
      : chosenInstance[0].instance.delayOperationLocalIds
        ? chosenInstance[0].instance.delayOperationLocalIds.some(e => e === this.instance.localId)
        : null;
  }

  checkJackingOperation(instance: DailyLogOperationInstance): boolean {
    return this.jackingActivityService.operationRequiresJackingActivity(instance);
  }

  presentSaveToast(): Promise<any> {
    const toast = this.toastCtrl.create({
      message:
        `Delay `
        + 'Operation \''
        + `${this.instance.projectSpecificOperation.name
          ? this.instance.projectSpecificOperation.name
          : this.instance.projectSpecificOperation.operationSummary.name}`
        + '\' added to the Daily Log',
      duration: 6000,
      position: 'bottom'
    });

    return toast.present();
  }

  allowedToPause(instance: DailyLogOperationInstance): boolean {

    if (instance.startTime.dateTime > this.instance.startTime.dateTime
      && !this.instance.endTime.getDate()) {
      return false;
    }

    return true;

  }

  async affectedSteps(instance: DailyLogOperationInstance): Promise<StepInstance[]> {
    const steps = !instance.stepCollectionInstance ? null : instance.stepCollectionInstance.stepInstances;

    if (!steps) {
      return [];
    }

    const stepsToReturn = steps.filter(p => {
      let affected = !p.exclude && this.operationService.operationInTime(
        p.startTime.dateTime,
        p.endTime.dateTime,
        this.instance.startTime.dateTime,
        this.instance.endTime.getDate());

      if (!affected) {
        affected = this.operationService.operationInTime(
          this.instance.startTime.dateTime,
          this.instance.endTime.dateTime,
          p.startTime.dateTime,
          p.endTime.getDate());
      }

      return affected;
    });

    for (let i = 0; i < stepsToReturn.length; i++) {
      stepsToReturn[i].startTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(stepsToReturn[i].startTime);
      stepsToReturn[i].endTimeToShow = stepsToReturn[i].endTime
        ? this.dateService.setTimeStringFromPtLuxonDate(stepsToReturn[i].endTime)
        : 'No End Time';
    }

    return stepsToReturn;
  }

  async checkExistingPauseTimesToRemove(delaySaveHelper: DelaySaveHelper): Promise<number[]> {
    // get list of pause times where delayId == this.instance.id
    // for each pause time, if operationInstanceId is not present in this.affectedOperations, then we want to delete the pause
    const result = [];

    const originalAffectedOperations = await this.delayService
      .findPotentiallyAffectedOperations(cloneDeep(this.originalInstanceCopy), this.dailyLogDate);

    if (originalAffectedOperations) {
      // an operation can have multiple pauses, so loop through
      for (let i = 0; i < originalAffectedOperations.length; i++) {
        for (let j = 0; j < originalAffectedOperations[i].pausedTimes.length; j++) {
          if (this.affectedConcurrentOperations.findIndex(a => a.instance.id === originalAffectedOperations[i].pausedTimes[j].operationInstanceApiId) === -1
            && this.affectedContractualOperations.findIndex(a => a.instance.id === originalAffectedOperations[i].pausedTimes[j].operationInstanceApiId) === -1) {
            result.push(originalAffectedOperations[i].pausedTimes[j].id);

            // if we are removing the pause here, it means that the paused operation was not found in affectedOperations above, which means that the usual process
            // that paused operations go through in delayService.pauseOperations wont happen, so we need to revert the pause (which will stitch any split steps together)
            // here, as well as ensure that the operation gets added to delaySaveHelper, so that the steps and daily log records will be saved
            delaySaveHelper.dailyLogIdsToDelete.concat(await this.stepService
              .revertPreviouslyAddedPause(originalAffectedOperations[i], cloneDeep(this.instance), this.originalInstanceCopy, delaySaveHelper));
            delaySaveHelper.pausedOperations.push(originalAffectedOperations[i]);
            delaySaveHelper = await this.delayService.saveAffectedOperationDailyLogRecordLogs(originalAffectedOperations[i], delaySaveHelper);
          }
        }
      }
    }

    return result;
  }

  private addEffect(effect: DelayAffect) {
    let matchingEffect = this.changedChoices.find(p => p.instance.id === effect.instance.id);

    if (!matchingEffect) {
      this.changedChoices.push(effect);
    } else {
      matchingEffect = cloneDeep(effect);
    }
  }
}



// WEBPACK FOOTER //
// ./src/pages/operations/affected-operations/affected-operations.ts